/**
 * Analytics Store
 * Manages analytics data with date range selection
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import {
  AnalyticsDateRange,
  EnergyDataPoint,
  RevenueDataPoint,
  AnalyticsSummary,
  TopLocation,
  DateRangePreset,
} from '../types/dashboard-types';

interface AnalyticsState {
  // Data
  energyData: EnergyDataPoint[];
  revenueData: RevenueDataPoint[];
  summary: AnalyticsSummary | null;
  topLocations: TopLocation[];

  // Top Locations settings
  topLocationsSortBy: 'revenue' | 'energy' | 'sessions';
  topLocationsLimit: number;

  // Date range
  dateRange: AnalyticsDateRange;
  selectedPreset: DateRangePreset;

  // Loading & error
  isLoading: boolean;
  isLoadingTopLocations: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // Actions
  loadAnalytics: () => Promise<void>;
  loadTopLocations: (sortBy?: 'revenue' | 'energy' | 'sessions', limit?: number) => Promise<void>;
  setDateRange: (dateRange: AnalyticsDateRange) => void;
  setPreset: (preset: DateRangePreset) => void;
  refresh: () => Promise<void>;
  reset: () => void;
}

/**
 * Get date range for a preset
 */
function getDateRangeForPreset(preset: DateRangePreset): AnalyticsDateRange {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];

  switch (preset) {
    case 'today': {
      return { dateFrom: endDate, dateTo: endDate };
    }
    case '7days': {
      const startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { dateFrom: startDate.toISOString().split('T')[0], dateTo: endDate };
    }
    case '30days':
    default: {
      const startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { dateFrom: startDate.toISOString().split('T')[0], dateTo: endDate };
    }
  }
}

const initialState = {
  energyData: [],
  revenueData: [],
  summary: null,
  topLocations: [],
  topLocationsSortBy: 'revenue' as 'revenue' | 'energy' | 'sessions',
  topLocationsLimit: 10,
  dateRange: getDateRangeForPreset('30days'),
  selectedPreset: '30days' as DateRangePreset,
  isLoading: false,
  isLoadingTopLocations: false,
  error: null,
  lastUpdated: null,
};

export const useAnalyticsStore = create<AnalyticsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      loadAnalytics: async () => {
        const { dateRange, topLocationsLimit, topLocationsSortBy } = get();
        set({ isLoading: true, isLoadingTopLocations: true, error: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getAnalyticsUseCase.execute(dateRange, topLocationsLimit, topLocationsSortBy);

          if (result.hasError) {
            set({
              error: result.errorMessage,
              isLoading: false,
              isLoadingTopLocations: false,
            });
            return;
          }

          set({
            energyData: result.energyData,
            revenueData: result.revenueData,
            summary: result.summary,
            topLocations: result.topLocations,
            isLoading: false,
            isLoadingTopLocations: false,
            lastUpdated: new Date(),
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load analytics',
            isLoading: false,
            isLoadingTopLocations: false,
          });
        }
      },

      loadTopLocations: async (sortBy, limit) => {
        const state = get();
        const newSortBy = sortBy ?? state.topLocationsSortBy;
        const newLimit = limit ?? state.topLocationsLimit;

        // Update state with new sort/limit and set loading (only for top locations)
        set({
          topLocationsSortBy: newSortBy,
          topLocationsLimit: newLimit,
          isLoadingTopLocations: true,
          error: null,
        });

        try {
          const services = servicesContainer.getServices();
          const repository = services.dashboardRepository;
          // Backend now handles sorting correctly
          const topLocations = await repository.getTopLocations(state.dateRange, newLimit, newSortBy);

          set({
            topLocations,
            isLoadingTopLocations: false,
          });
        } catch (error) {
          console.error('Failed to load top locations:', error);
          set({
            error: error instanceof Error ? error.message : 'Failed to load top locations',
            isLoadingTopLocations: false,
          });
        }
      },

      setDateRange: (dateRange: AnalyticsDateRange) => {
        set({ dateRange, selectedPreset: 'custom' });
        get().loadAnalytics();
      },

      setPreset: (preset: DateRangePreset) => {
        const dateRange = getDateRangeForPreset(preset);
        set({ dateRange, selectedPreset: preset });
        get().loadAnalytics();
      },

      refresh: async () => {
        await get().loadAnalytics();
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'analytics-storage',
      version: 4,
      partialize: (state) => ({
        topLocationsSortBy: state.topLocationsSortBy,
        topLocationsLimit: state.topLocationsLimit,
        dateRange: state.dateRange,
        selectedPreset: state.selectedPreset,
      }),
    }
  )
);
